import { ComponentProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, VStack, Button } from 'native-base';
import { find, first, isEmpty, range, lt } from 'lodash';
import moment from 'moment';

import { Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { GetReportDataForSiteQueryParams, ReportScheduleTypeEnum, StoreReportInputDto } from '@pimm/services/lib/store-inspections/types';
import { formatToISOString } from '@app/utils/date-formatter';
import { useReviewVerifyReports } from '../context/review-verify-reports.context';
import { StoreReport, StoreReportEntry, WeekFocus } from '../hooks/combine-store-reports.hook';
import { ReviewInfoSnippet } from './review-info-snippet';
import { StoreReportTableRow, TableCell } from './store-report-table-row';

type StoreReportTableProps = Pick<ComponentProps<typeof StoreReportTableRow>, 'size'> & {
  isDownloadVisible?: boolean;
  isEditEnabled?: boolean;
  report: StoreReport;
  selectedId?: string;
  week: WeekFocus;
  moduleType: number;
  onSelect: (_query: Partial<GetReportDataForSiteQueryParams>) => void;
};

export const StoreReportTable = ({
  isDownloadVisible = false,
  isEditEnabled = false,
  report,
  selectedId,
  size = 'xl',
  week,
  moduleType,
  ..._props
}: StoreReportTableProps) => {
  const { t } = useTranslation();
  const { locale, translate } = useAppLocale();
  const { setOpen: reviewVerify } = useReviewVerifyReports();

  const dateNow = moment().format('YYYY-MM-DD');
  const isDaily = report.scheduleType === ReportScheduleTypeEnum.Daily;
  const isVerified = report.entries.every(_ => !!_.dmReview.value);

  const handleVerify = (_storeReports?: StoreReportInputDto[]) => {
    // prettier-ignore
    // TODO: need to revisit
    reviewVerify([{
      reportId: report.reportId,
      scheduleType: report.scheduleType,
      storeReports: _storeReports ?? report.entries.reduce((_inputs: StoreReportInputDto[], _) => {
        if (!_.totalForms.value || !!_.eodReview.value) return _inputs
        return [..._inputs, { date: _.date.value, storeReportId: _.id }]
      }, [])
    }]);
  };

  // Memoize Table Columns
  const columns: TableCell[] = useMemo(() => {
    return [
      {
        cellProps: { p: 0, minWidth: size === 'xl' ? 220 : 200 },
        renderHeader: _row => t(_row.entry.date.key),
        renderCell: _row => {
          const date = _row.entry.date.value ? moment(_row.entry.date.value) : undefined;
          let displayText = date?.format('dddd (MM/DD/YY)');

          if (!isDaily && _row.level === 1) {
            const startDate = moment(week.startDate);
            displayText = `Wk ${week.weekNumber} (${startDate.format('MMM DD')} - ${startDate.clone().add(6, 'days').format('MMM DD')})`;
          }

          return (
            <Box pl={(_row.level - 1) * 3}>
              <Text size="md" fontWeight={400} color="gray.900">
                {displayText}
              </Text>
            </Box>
          );
        },
      },
      {
        dataField: 'totalForms.value',
        hidden: size === 'md',
        renderHeader: _row => t(_row.entry.totalForms.key),
      },
      {
        dataField: 'completionPercentage.value',
        renderHeader: _row => t(_row.entry.completionPercentage.key),
        renderCell: _row => {
          if (!_row.entry.totalForms?.value) return '-';
          return (
            <HStack space={2} alignItems="center" pr={3}>
              <Text size="md" fontWeight={500} color="gray.900" lineHeight="md">
                {_row.entry.completionPercentage?.value}
              </Text>
            </HStack>
          );
        },
      },
      { dataField: 'completed.value', renderHeader: _row => t(_row.entry.completed.key), hidden: size === 'md' },
      { dataField: 'partiallyCompleted.value', renderHeader: _row => t(_row.entry.partiallyCompleted.key), hidden: size === 'md' },
      { dataField: 'notStarted.value', renderHeader: _row => t(_row.entry.notStarted.key), hidden: size === 'md' },
      { dataField: 'lateItems.value', renderHeader: _row => t(_row.entry.lateItems.key), hidden: size === 'md' },
      {
        cellProps: { minWidth: size === 'xl' ? 160 : 150 },
        renderHeader: _row => t(_row.entry.eodReview.key),
        hidden: moduleType === 2,
        renderCell: ({ entry: { date, eodReview, entries = [], id }, isDisabled }) => {
          if (!isEmpty(entries)) {
            const verified = entries.filter(({ eodReview }) => !!eodReview.value?.reviewTimestampUtc).length;
            if (entries.length > 1) {
              return (
                <Text size="md" fontWeight={500} color="gray.900" lineHeight="md">
                  {`${verified}/${entries.length}`}
                </Text>
              );
            }
            const { eodReview: entryEodReview } = first(entries) as StoreReportEntry;
            return entryEodReview?.value?.reviewTimestampUtc ? <ReviewInfoSnippet reviewer={entryEodReview.value} /> : '-';
          }
          const day = typeof date?.value === 'string' ? date.value.slice(0, 10) : undefined;
          const isPast = !!day && lt(day, dateNow);

          if (!eodReview?.value?.reviewTimestampUtc && isPast && isEditEnabled && !isDisabled) {
            // return '-';
            // TODO: need to revisit
            return (
              <Button
                variant="unstyled"
                size="sm"
                shadow={0}
                _text={{ fontSize: 'md' }}
                w="full"
                maxWidth="80px"
                borderColor="gray.300"
                bg="white"
                onPress={() => handleVerify([{ date: date?.value, storeReportId: id }])}
              >
                {t('store_inspections:verify')}
              </Button>
            );
          }
          return eodReview?.value?.reviewTimestampUtc ? <ReviewInfoSnippet reviewer={eodReview.value} /> : '-';
        },
      },
      {
        cellProps: { minWidth: size === 'xl' ? 160 : 150 },
        renderHeader: _row => t(_row.entry.dmReview.key),
        hidden: moduleType === 2,
        renderCell: ({ entry: { dmReview, ...entry }, level }) => {
          if (level === 2) return '';
          if (dmReview?.value) return <ReviewInfoSnippet reviewer={dmReview.value} />;
          return '-';
        },
      },
    ];
  }, [locale, report]);

  return (
    <VStack rounded="lg" borderWidth={1} borderColor="gray.300" bg="white" overflow="hidden">
      <HStack space={2} alignItems="center" py={2} px={3} minH={12} borderBottomWidth={1}>
        <VStack flex={1}>
          <Text size="lg" fontWeight={700} color="black" lineHeight="lg">
            {translate(report.name, report.translations)}
          </Text>
          <Text size="md" fontWeight={400} color="gray.600" lineHeight="sm">
            {`${t('store_inspections:schedule_type')}: ${t(`store_inspections:${report.scheduleType.toLowerCase()}`)}`}
          </Text>
        </VStack>

        {/*
        TODO: need to revisit
        {isDaily && isEditEnabled && !isVerified && (
          <Button
            variant="unstyled"
            size="sm"
            shadow={0}
            _text={{ fontSize: 'md' }}
            minW="90px"
            borderColor="gray.300"
            onPress={() => handleVerify()}
          >
            {t('store_inspections:verify_all')}
          </Button>
        )} */}
      </HStack>

      <VStack>
        {/* Header Columns */}
        <HStack alignItems="center" px={1} bg="gray.100">
          {size === 'xl' && <Box alignItems="center" justifyContent="center" minH={9} w={8} />}
          {columns.map((_column, index) => {
            if (_column.hidden) return null;
            const entry = first(report.entries) ?? ({} as StoreReportEntry);
            const header = _column.renderHeader?.({ entry, level: 1 }) ?? _column.header ?? '';
            return (
              <Box
                key={`${report.reportId}.column.${index}`}
                flex={1}
                justifyContent="center"
                minHeight={8}
                py={1}
                px={2}
                {..._column.cellProps}
              >
                {typeof header === 'string' || typeof header === 'number' ? (
                  <Text size="sm" fontWeight={600} color="gray.700">
                    {header}
                  </Text>
                ) : (
                  header
                )}
              </Box>
            );
          })}
        </HStack>

        {range(0, isDaily ? 7 : 1).map((_day, index) => {
          const date = moment(week.startDate).add(_day, 'days').toDate();
          const isoDate = formatToISOString(date, true);
          let entry = report.entries.find(_ => (isDaily ? _.date.value === isoDate : !!_.totalForms.value));

          if (!entry) entry = { date: { value: isoDate } } as StoreReportEntry;

          return (
            <StoreReportTableRow
              key={`${report.reportId}.${index}`}
              index={index}
              columns={columns}
              entry={entry}
              size={size}
              isActive={!!selectedId && entry?.id === selectedId && (isDaily || index === 0)}
              isDisabled={!entry.totalForms || isoDate > dateNow}
              onPress={() => _props.onSelect({ date: isoDate, storeReportId: entry.id! })}
            />
          );
        })}
      </VStack>
    </VStack>
  );
};
